import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { defaultTheme, ThemeContext } from "@utils/ThemeContext";
import { ThemeModel } from "../../types/ThemeModel";
import cx from "classnames";
import { mergeRefs } from "@utils/mergeRefs";
import {
  motion,
  useAnimate,
  useMotionValue,
  useMotionValueEvent,
} from "motion/react";
import { usePaletteValues } from "@hooks/usePaletteValues";
import { animationCurves } from "@utils/animationCurves";

/**
 * The provider to manage contextual themes. This provides a ThemeContext to all elements below
 * in the React tree, but it also uses CSS variables applied via our custom Tailwind theming plugin
 * to ensure the theme is available in the native element tree, by using the *-palette-current-* class
 * namespace. For instance, if you want to access the expected background color for the current theme
 * and set that background to an element, you can give that element the class bg-palette-current-background.
 * If you wanted to set the text to the current expect foreground color, you can use the class
 * text-palette-current-foreground.
 *
 * The ThemeModel relies on three parts. The palette, the foreground color, and the background color.
 * The palette is selected from a standard set created by the project UX team. Each palette has
 * a light, highlight, true, deep, and dark level. We also generally include neutral-dark & neutral
 * light as levels in every palette despite the fact that they are always the same brand colors.
 *
 * So if you wanted a neutral-dark background with the Netflix brand red theme, you would create this theme:
 *
 * {
 *   palette: "red",
 *   background: "neutral-dark",
 *   foreground: "true"
 * }
 *
 * The "true" (string, not boolean) foreground is a reference to the "true" level on the "red" palette.
 *
 * @param theme A given ThemeModel to be applied below.
 *     Using this prop makes the component act "controlled"
 * @param children All elements beholden to the given theme
 * @param root Used to identify if it's the root theme or a sub-theme.
 *     Should only be applied true in the App components
 */
export const ThemeContextProvider = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    theme?: ThemeModel;
    root?: boolean;
    presetBackground?: boolean;
  } & ComponentProps<typeof motion.div>
>(
  (
    { theme: themeProp, children, root, presetBackground = root, ...props },
    ref
  ) => {
    const [, animate] = useAnimate();
    const refObject = useRef<HTMLElement>(null);
    const [theme, setTheme] = useState<ThemeModel>(themeProp ?? defaultTheme);

    useEffect(() => {
      if (themeProp) {
        setTheme(themeProp);
      }
    }, [themeProp]);

    const palettesValues = usePaletteValues(theme.palette);

    const backgroundColor = useMotionValue(palettesValues[theme.background]());

    useMotionValueEvent(backgroundColor, "change", (backgroundColor) => {
      if (!root || !presetBackground) return;
      document.body.style.backgroundColor = backgroundColor;
    });

    // Ensure the background color is animated
    useLayoutEffect(() => {
      animate(
        backgroundColor,
        palettesValues[theme.background](),
        animationCurves.default
      );
    }, [theme, backgroundColor, palettesValues, animate]);

    const value = useMemo(
      () => ({ theme, setTheme, containerRef: refObject, backgroundColor }),
      [theme, refObject, backgroundColor]
    );

    return (
      <ThemeContext.Provider value={value}>
        <motion.div
          {...props}
          ref={mergeRefs(ref, refObject)}
          className={cx(
            themeSetterClassName[theme.palette],
            backgroundSetterClassName[theme.background],
            foregroundSetterClassName[theme.foreground],
            "text-palette-current-foreground transition-[color]",
            props.className
          )}
          style={{
            ...props.style,
            ...(presetBackground && { backgroundColor }),
          }}
        >
          {children}
        </motion.div>
      </ThemeContext.Provider>
    );
  }
);

const themeSetterClassName: Record<ThemeModel["palette"], string> = {
  "yellow-orange": "set-current-palette-yellow-orange",
  red: "set-current-palette-red",
  "red-deep": "set-current-palette-red-deep",
  fuchsia: "set-current-palette-fuchsia",
  purple: "set-current-palette-purple",
  violet: "set-current-palette-violet",
  blue: "set-current-palette-blue",
  cyan: "set-current-palette-cyan",
  green: "set-current-palette-green",
  "red-mx": "set-current-palette-red-mx",
};

const backgroundSetterClassName: Record<
  ThemeModel["background"],
  `set-current-background-${ThemeModel["background"]}`
> = {
  light: "set-current-background-light",
  highlight: "set-current-background-highlight",
  true: "set-current-background-true",
  deep: "set-current-background-deep",
  dark: "set-current-background-dark",
  "neutral-dark": "set-current-background-neutral-dark",
  "neutral-light": "set-current-background-neutral-light",
};

const foregroundSetterClassName: Record<
  ThemeModel["foreground"],
  `set-current-foreground-${ThemeModel["foreground"]}`
> = {
  light: "set-current-foreground-light",
  highlight: "set-current-foreground-highlight",
  true: "set-current-foreground-true",
  deep: "set-current-foreground-deep",
  dark: "set-current-foreground-dark",
  "neutral-dark": "set-current-foreground-neutral-dark",
  "neutral-light": "set-current-foreground-neutral-light",
};
